import { type InputHTMLAttributes, type ReactNode, useId } from 'react';

import { classlist } from '~/_shared/design-system/lib/classlist';

import './text-input.css';

type TextInputSize = 'sm' | 'md' | 'lg';

interface TextInputProps
	extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
	size?: TextInputSize;
	is_invalid?: boolean;
	/**
	 * Optional icon rendered inside the input on the leading edge. The
	 * caller passes any ReactNode (typically an `<Icon name='…' />`);
	 * the component wraps the input + slot in a positioned shell and
	 * indents the text via the `text-input--has-start-icon` modifier.
	 * The slot element exposes `data-slot="start"` for downstream styling.
	 */
	start_icon?: ReactNode;
	/** Companion to start_icon, rendered on the trailing edge. */
	end_icon?: ReactNode;
}

const TextInput = ({
	size = 'md',
	is_invalid = false,
	id,
	className,
	type = 'text',
	'aria-invalid': ariaInvalidProp,
	start_icon,
	end_icon,
	...rest
}: TextInputProps) => {
	const has_start = start_icon !== undefined;
	const has_end = end_icon !== undefined;
	// Instance-unique default id (explicit `id` prop wins) — see BaseButton.
	const auto_id = useId();
	const input = (
		<input
			{...rest}
			id={id ?? `TextInput-${auto_id}`}
			type={type}
			aria-invalid={is_invalid || ariaInvalidProp || undefined}
			className={classlist(
				'text-input',
				`text-input--${size}`,
				is_invalid && 'text-input--invalid',
				has_start && 'text-input--has-start-icon',
				has_end && 'text-input--has-end-icon',
				className,
			)}
		/>
	);

	// Bare input when there are no slots — preserves the simple shape
	// existing consumers (LabeledInput, LoginPage) already render. The
	// shell wrapper only appears when an icon slot is requested.
	if (!has_start && !has_end) return input;

	return (
		<div className='text-input-shell'>
			{has_start ? (
				<span
					data-slot='start'
					aria-hidden='true'
					className='text-input__slot text-input__slot--start'
				>
					{start_icon}
				</span>
			) : null}
			{input}
			{has_end ? (
				<span
					data-slot='end'
					aria-hidden='true'
					className='text-input__slot text-input__slot--end'
				>
					{end_icon}
				</span>
			) : null}
		</div>
	);
};

export type { TextInputProps, TextInputSize };
export { TextInput };
